import React, { useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Trash2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { format } from "date-fns";
import DeleteMappingDialog from "./DeleteMappingDialog";

function ConfidencePill({ value }) {
  const pct = Math.round((value || 0) * 100);
  let color, bg;
  if (pct >= 90) { color = 'rgba(77,179,0,0.90)';    bg = 'rgba(77,179,0,0.15)'; }
  else if (pct >= 70) { color = 'rgba(233,171,48,0.90)'; bg = 'rgba(233,171,48,0.20)'; }
  else { color = 'rgba(222,111,107,0.90)'; bg = 'rgba(222,111,107,0.20)'; }
  return (
    <span
      className="inline-flex items-center gap-1 h-[18px] rounded-[9px] whitespace-nowrap leading-none text-xs font-medium"
      style={{ background: bg, color, padding: '2px 8px 2px 3px' }}
    >
      <span className="size-[10px] rounded-full shrink-0" style={{ background: color }} />
      {pct}%
    </span>
  );
}

const COL = {
  field:    'flex-1 min-w-0',
  source:   'w-[160px] shrink-0',
  element:  'w-[220px] shrink-0',
  conf:     'w-[100px] shrink-0',
  date:     'w-[120px] shrink-0',
  actions:  'w-[60px]  shrink-0',
};

export default function MappingsTable({ mappings, isLoading, onMappingUpdate, onEditMapping, onDeleteMapping }) {
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState(null);

  const handleDeleteClick = (mapping) => { setMappingToDelete(mapping); setDeleteDialogOpen(true); };
  const handleDeleteSuccess = () => {
    setDeleteDialogOpen(false);
    setMappingToDelete(null);
    onMappingUpdate?.();
  };

  if (isLoading) {
    return (
      <div className="flex flex-col min-w-[1000px]">
        <div className="h-9 rounded-t-[8px]" style={{ background: 'var(--table-header-bg)' }} />
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-11 px-5 flex items-center" style={{ background: i % 2 === 0 ? 'var(--card-bg)' : 'var(--table-stripe)' }}>
            <Skeleton className="h-4 w-48" />
          </div>
        ))}
      </div>
    );
  }

  if (!mappings.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-2">
        <p className="text-sm text-page-muted">No mappings found</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col min-w-[1000px]">
        {/* Header */}
        <div
          className="flex items-center h-9 px-5 gap-[10px]"
          style={{ background: 'var(--table-header-bg)', borderRadius: '8px 8px 0 0' }}
        >
          <div className={`${COL.field} text-sm font-medium`} style={{ color: '#377AD6' }}>Source field</div>
          <div className={`${COL.source} text-sm font-medium`} style={{ color: '#377AD6' }}>Data source</div>
          <div className={`${COL.element} text-sm font-medium`} style={{ color: '#377AD6' }}>Canonical element</div>
          <div className={`${COL.conf} text-sm font-medium`} style={{ color: '#377AD6' }}>Confidence</div>
          <div className={`${COL.date} text-sm font-medium`} style={{ color: '#377AD6' }}>Last validated</div>
          <div className={COL.actions} />
        </div>

        {/* Rows */}
        {mappings.map((mapping, i) => {
          const rowBg = i % 2 === 0 ? 'var(--table-stripe)' : 'var(--card-bg)';
          return (
            <div
              key={mapping.id}
              className="group flex items-center h-11 px-5 gap-[10px] cursor-pointer"
              style={{ background: rowBg }}
              onClick={() => navigate(createPageUrl(`source-mappings/${mapping.id}`))}
            >
              <div className={`${COL.field} min-w-0`}>
                <div className="text-sm font-medium truncate text-page-text group-hover:text-nav-active transition-colors">
                  {mapping.source_field_name}
                </div>
                {mapping.transformation_notes && (
                  <div className="text-xs truncate" style={{ color: 'var(--page-muted)' }}>
                    {mapping.transformation_notes}
                  </div>
                )}
              </div>
              <div className={COL.source}>
                <span className="text-sm truncate block" style={{ color: 'var(--page-text)' }}>
                  {mapping.source_name || '—'}
                </span>
              </div>
              <div className={COL.element} onClick={(e) => e.stopPropagation()}>
                {mapping.canonical_element_id ? (
                  <Link
                    to={createPageUrl(`element-details?id=${mapping.canonical_element_id}`)}
                    className="text-sm font-medium truncate block no-underline hover:underline"
                    style={{ color: '#377AD6' }}
                    title={mapping.element_canonical_name}
                  >
                    {mapping.element_canonical_name || `Element ${mapping.canonical_element_id}`}
                  </Link>
                ) : (
                  <span className="text-sm" style={{ color: 'var(--page-muted)' }}>—</span>
                )}
              </div>
              <div className={COL.conf}>
                <ConfidencePill value={mapping.mapping_confidence} />
              </div>
              <div className={COL.date}>
                <span className="text-sm" style={{ color: 'var(--page-text)' }}>
                  {mapping.last_validated
                    ? format(new Date(mapping.last_validated), 'MMM d, yyyy')
                    : <span style={{ color: 'var(--page-muted)' }}>Never</span>}
                </span>
              </div>
              <div className={`${COL.actions} flex justify-end`} onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => handleDeleteClick(mapping)}
                  title="Delete mapping"
                  className="flex items-center justify-center bg-transparent border-0 cursor-pointer text-nav-hover hover:text-nav-active transition-colors"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <DeleteMappingDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onSuccess={handleDeleteSuccess}
        mapping={mappingToDelete}
      />
    </>
  );
}
